import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { validateLength } from '../../utils/validation';
import { sendSignInWithAccountCodeRequest } from '../../utils/api/accountApi';
import { genericErrorMessage } from '../../utils/lang/commonErrors';
import { isApiError } from '../../utils/api/clearApiClient';
import { getParsedParams } from '../../utils/consts/params';
import { Button } from '../common/Button';
import { grpcResponceCodes } from '../../utils/consts/apiCodes';
import InputField from './fields/InputField';
import styles from './AccountCodeForm.scss';

const ACCOUNT_CODE_LENGTH = 8;

const getEnterLink = () => {
  const LinkWrapper = (chunk: React.ReactNode) => <Link to="/enter">{chunk}</Link>;

  return LinkWrapper;
};

const AccountCodeForm = () => {
  const navigate = useNavigate();

  const [codeValue, setCodeValue] = useState('');
  const onCodeChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setCodeValue(e.target.value);
  };

  const [isLoading, setIsLoading] = useState(false);

  const [formErrors, setFormErrors] = useState<{ code?: string }>({});

  const intl = useIntl();

  const onSubmit = (code = '') => {
    if (code.length === 0) {
      code = codeValue;
    }

    setIsLoading(true);
    const codeError = validateLength(code, ACCOUNT_CODE_LENGTH);

    if (codeError) {
      setFormErrors({
        code: codeError,
      });
      setIsLoading(false);
    } else {
      sendSignInWithAccountCodeRequest(code).then(
        async () => {
          await navigate('/profile');
        },
        (error: unknown) => {
          console.error('Sign Up request failed', error);
          if (
            isApiError(error) &&
            (error.response?.data.code === grpcResponceCodes.INVALID_ARGUMENT ||
              error.response?.data.code === grpcResponceCodes.UNAUTHENTICATED)
          ) {
            setFormErrors({
              code: intl.formatMessage({
                id: 'accountCodeEnter.form.error.invalidCode',
                defaultMessage: 'Invalid code',
              }),
            });
          } else {
            setFormErrors({
              code: intl.formatMessage(genericErrorMessage),
            });
          }
          setIsLoading(false);
        },
      );
    }
  };

  useEffect(() => {
    const params = getParsedParams();

    if (params.PROMOCODE) {
      // it's access code, not a promo code here
      setCodeValue(params.PROMOCODE);
      onSubmit(params.PROMOCODE);
    }
  }, []);

  return (
    <div className={styles.wrapper}>
      <form className={styles.form}>
        <h2 className={styles.title}>
          <FormattedMessage
            id="accountCodeEnter.title"
            defaultMessage="Have an account code?"
            description="Title of the account code page"
          />
        </h2>
        <p className={styles.subtitle}>
          <FormattedMessage
            id="accountCodeEnter.subtitle"
            defaultMessage="If you already have an account, please, <link>Log In</link>"
            description="Subtitle of the account code page"
            values={{
              link: getEnterLink(),
            }}
          />
        </p>

        <InputField
          value={codeValue}
          error={formErrors.code}
          placeholder={intl.formatMessage({
            id: 'accountCodeEnter.form.placeholder',
            defaultMessage: 'Account code',
            description: 'Placeholder of the account code field within the account code form',
          })}
          onChange={onCodeChange}
        />

        <Button loading={isLoading} className={styles.cta} type="submit" onClick={onSubmit}>
          {intl.formatMessage({
            id: 'accountCodeEnter.form.submitButton',
            defaultMessage: 'Enter',
            description: 'Content of the submit button of the the account code form',
          })}
        </Button>
      </form>
    </div>
  );
};

export default AccountCodeForm;
